// The app shell. The sidebar IS the vaults list (the everyday surface) + a
// "New vault" action; clicking a vault opens it in the content area while the
// list stays for one-click switching. Occasional, role-gated destinations
// (Account always, Users member+, Server admin) live in the footer user
// popover, with the theme toggle grouped below them, so it's empty of admin
// items for people who can't use them. Docs is a first-class footer item just
// above that trigger — always visible, not buried in the popover.
// Icon-collapsible; the top bar carries a breadcrumb + actions slot
// (filled by pages via useShellHeader). See decisions/90.

import { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  IconBook2,
  IconLogout,
  IconMoon,
  IconPlus,
  IconSelector,
  IconServer2,
  IconSun,
  IconUserCircle,
  IconUsers,
} from "@tabler/icons-react";
import { listVaults, logout } from "../api";
import { useApi, useUser } from "../session";
import { useAsyncData } from "../lib/use-async";
import { useDelayedLoading } from "../lib/use-delayed-loading";
import { hasServerRole } from "./roles";
import { NAV_ROUTES } from "./nav";
import { initials } from "./initials";
import { HeaderSlotCtx, type HeaderSlot } from "./shell-header";
import { useTheme } from "../theme";
import { CairnMark } from "@/components/cairn-mark";
import { CreateVaultDialog } from "@/components/create-vault-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";

// Show the quick-start docs page once, on the first authed load, then never
// auto-open it again. localStorage only — no backend. It stays reachable any
// time from the sidebar's "Docs" item.
const QUICKSTART_SEEN_KEY = "cairn.quickstart-seen";

function pageTitle(pathname: string): string {
  if (pathname.startsWith("/account")) return "Account";
  const match = NAV_ROUTES.filter((r) => pathname.startsWith(r.path)).sort(
    (a, b) => b.path.length - a.path.length,
  )[0];
  return match?.label ?? "Cairn";
}

export function AppShell() {
  const user = useUser();
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const [slot, setSlot] = useState<HeaderSlot>({});
  const setHeaderSlot = useCallback((s: HeaderSlot) => setSlot(s), []);

  // First-login nudge: send a brand-new visitor to the quick-start once, then
  // set the flag so it never auto-shows again. Only fires when they landed on
  // the index route ("/") with no destination in mind — a deep link to a real
  // page (/v/:vaultId, /account, /docs/…, …) is an explicit intent we must
  // not clobber, since a `replace` there would strand the requested route with
  // no way Back could recover it. No-op when storage is unavailable so nobody
  // gets stranded. Re-runs on navigation but the flag + path guard keep it a
  // single, non-looping redirect.
  useEffect(() => {
    if (location.pathname !== "/") return;
    try {
      if (localStorage.getItem(QUICKSTART_SEEN_KEY)) return;
      localStorage.setItem(QUICKSTART_SEEN_KEY, "1");
    } catch {
      return;
    }
    navigate("/docs/quickstart", { replace: true });
  }, [navigate, location.pathname]);

  const vaults = useAsyncData(() => listVaults(api), []);
  const showVaultsLoading = useDelayedLoading(vaults.loading && !vaults.data);
  const canCreate = hasServerRole(user.server_role, "member");

  async function signOut() {
    try {
      await logout(api);
    } finally {
      navigate("/login", { replace: true });
    }
  }

  return (
    <SidebarProvider>
      <Sidebar variant="inset" collapsible="icon">
        <SidebarHeader>
          <Link
            to="/"
            className="flex items-center gap-2 px-2 py-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0"
          >
            <CairnMark className="size-6 shrink-0 text-primary" title="Cairn" />
            {/* Clip the wordmark to width+opacity (not display:none) so it reveals
                in sync with the rail width on expand instead of popping in. */}
            <div className="flex max-w-40 flex-col overflow-hidden leading-none opacity-100 transition-[max-width,opacity] duration-200 group-data-[collapsible=icon]:max-w-0 group-data-[collapsible=icon]:opacity-0">
              <span className="text-base font-bold tracking-tight">cairn</span>
              <span className="mt-0.5 font-mono text-[0.625rem] tracking-wider text-muted-foreground uppercase">
                self-hosted
              </span>
            </div>
          </Link>
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              {canCreate && (
                <SidebarMenuItem>
                  <CreateVaultDialog
                    onCreated={vaults.reload}
                    trigger={
                      <SidebarMenuButton tooltip="New vault" className="text-muted-foreground">
                        <IconPlus />
                        <span>New vault</span>
                      </SidebarMenuButton>
                    }
                  />
                </SidebarMenuItem>
              )}

              {showVaultsLoading &&
                !vaults.data &&
                [0, 1, 2].map((i) => (
                  <SidebarMenuItem key={i}>
                    <div className="flex items-center gap-2 px-2 py-1.5 group-data-[collapsible=icon]:hidden">
                      <Skeleton className="size-5 rounded" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </SidebarMenuItem>
                ))}

              {vaults.data?.length === 0 && (
                <p className="px-2 py-1.5 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
                  No vaults yet.
                </p>
              )}

              {vaults.data?.map(({ vault }) => {
                const base = `/v/${vault.id}`;
                const active =
                  location.pathname === base || location.pathname.startsWith(base + "/");
                return (
                  <SidebarMenuItem key={vault.id}>
                    <SidebarMenuButton
                      isActive={active}
                      tooltip={vault.name}
                      render={<NavLink to={base} />}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center rounded bg-muted text-[0.625rem] font-semibold text-foreground group-data-[collapsible=icon]:size-4">
                        {vault.name.slice(0, 1).toUpperCase()}
                      </span>
                      <span className="truncate">{vault.name}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={location.pathname.startsWith("/docs")}
                tooltip="Docs"
                className="rounded-full"
                render={<NavLink to="/docs" />}
              >
                <IconBook2 />
                <span>Docs</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <SidebarMenuButton
                  size="lg"
                  render={<DropdownMenuTrigger />}
                  aria-label="Account menu"
                >
                  <Avatar className="size-8">
                    <AvatarFallback className="text-xs">
                      {initials(user.display_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex min-w-0 flex-1 flex-col text-left leading-tight">
                    <span className="truncate text-sm font-medium">{user.display_name}</span>
                    <span className="truncate font-mono text-xs text-muted-foreground">
                      {user.server_role}
                    </span>
                  </div>
                  <IconSelector className="ml-auto text-muted-foreground" />
                </SidebarMenuButton>
                <DropdownMenuContent side="right" align="end" className="w-56">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-[0.625rem] tracking-wider text-muted-foreground uppercase">
                          signed in as
                        </span>
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium">
                            {user.display_name}
                          </span>
                          <Badge variant="secondary" className="shrink-0 font-mono">
                            {user.server_role}
                          </Badge>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem onClick={() => navigate("/account")}>
                      <IconUserCircle />
                      Account
                    </DropdownMenuItem>
                    {hasServerRole(user.server_role, "member") && (
                      <DropdownMenuItem onClick={() => navigate("/users")}>
                        <IconUsers />
                        Users
                      </DropdownMenuItem>
                    )}
                    {hasServerRole(user.server_role, "admin") && (
                      <DropdownMenuItem onClick={() => navigate("/server")}>
                        <IconServer2 />
                        Server
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem onClick={toggle}>
                      {theme === "dark" ? <IconMoon /> : <IconSun />}
                      Switch to {theme === "dark" ? "light" : "dark"} theme
                    </DropdownMenuItem>
                    <DropdownMenuItem variant="destructive" onClick={() => void signOut()}>
                      <IconLogout />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>

      <SidebarInset className="overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          {slot.crumbs ?? (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>{pageTitle(location.pathname)}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          )}
          {slot.actions && <div className="ml-auto flex items-center gap-2">{slot.actions}</div>}
        </header>
        <main className="min-w-0 flex-1">
          <HeaderSlotCtx.Provider value={setHeaderSlot}>
            <Outlet />
          </HeaderSlotCtx.Provider>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
